import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import PatientInformation from "./patient/PatientInformation";
import { Tabs, Tab } from "@mui/material";
import PatientStudies from "./patient/PatientStudies";
import PatientDetailedEHR from "./patient/PatientDetailedEHR";
import EditPatientModal from "./shared/EditPatientModal";
import dayjs from "dayjs";

const Container = styled.div`
  width: 100%;
  background-color: #000;
  border-radius: 8px;
  color: white;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
`;

const Header = styled.div`
  background-color: #0088CF;
  display: flex;
  justify-content: space-between;
  padding: 0px 10px;
  align-items: center;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 5;
`;

const HeaderTitle = styled.div`
  padding: 5px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 30px;
`;

const MessageContainer = styled.div`
  padding: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
`;

const StyledTabs = styled(Tabs)`
  border-bottom: 1px solid #A5E0FF;
  height: 38px !important;
  min-height: 38px !important;

  & .MuiTabs-indicator {
    background-color: #A5E0FF;
    height: 4px;
  }

  & .MuiTabs-flexContainer {
    gap: 8px;
  }

  & .MuiTab-root {
    height: 38px !important;
    min-height: 38px !important;
    color: white;
    font-size: 16px;
    font-weight: 400;
    text-transform: capitalize;
    width: 180px;
    gap: 4px;
    padding: 8px 12px;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    background-color: #A5E0FF33;
    color: #DEDFE0 !important;
  }

  & .Mui-selected {
    font-weight: bold;
    background-color: #A5E0FF;
    color: #2B3342 !important;
    padding: 0px;
  }
`;

const StyledTab = styled(Tab)`
  color: #fbfeff;

  &.Mui-selected {
    font-weight: bold;
    color: #7efbfd;
  }
`;

const MainContent = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 36px 24px 0px 24px;
  border-radius: 8px 8px 0px 0px;
  display: flex;
  flex-direction: column;
`;

const PatientDetailsComponent = ({
  patient,
  containerWidth,
  setSelectedPatient,
  openIframeViewer,
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const patientStudiesRef = useRef(null);
  const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false);
  const [studyUIDs, setStudyUIDs] = useState("");

  useEffect(() => {
    if (studyUIDs && patient?.uuid) {
      setSelectedPatient({ ...patient, studyUID: studyUIDs });
    }
  }, [studyUIDs]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const extendedPatient = useMemo(() => {
    if (!patient) return null;
    return { ...patient, setStudyUIDs };
  }, [patient, setStudyUIDs]);

  const renderTabContent = () => {
    switch (selectedTab) {
      case 2:
        return <PatientInformation patient={patient} />;
      case 1:
        return <PatientDetailedEHR patient={patient} />;
      case 0:
        return (
          <PatientStudies
            ref={patientStudiesRef}
            patient={extendedPatient}
            openIframeViewer={openIframeViewer}
          />
        );
      default:
        return null;
    }
  };

  if (!patient) {
    return (
      <div>
        <MessageContainer>Select a patient to see details</MessageContainer>
      </div>
    );
  }

  return (
    <Container containerWidth={containerWidth}>
      <Header>
        <HeaderTitle>
          <h3 style={{ margin: "5px", textTransform: "capitalize" }}>
            {`${patient.last_name.toUpperCase()}, ${patient.first_name.toUpperCase()}  `}
          </h3>
          <p style={{ marginLeft: "5px", margin: "auto 0" }}>
            DOB:{" "}
            <strong>
              {patient.date_of_birth
                ? dayjs(patient.date_of_birth).format("MM/DD/YYYY")
                : "N/A"}
            </strong>
          </p>
          <p style={{ marginLeft: "5px", margin: "auto 0" }}>
            Patient ID: <strong>{patient.client_patient_uuid}</strong>
          </p>
          <p style={{ marginLeft: "5px", margin: "auto 0" }}>
            Gender: <strong>{patient.gender.toUpperCase()}</strong>
          </p>
        </HeaderTitle>
      </Header>
      <MainContent>
        <StyledTabs value={selectedTab} onChange={handleTabChange}>
          <StyledTab label="Imaging" />
          <StyledTab label="External Documents" />
          <StyledTab label="Patient Information" />
        </StyledTabs>
        {renderTabContent()}
      </MainContent>

      <EditPatientModal
        open={isEditPatientModalOpen}
        onClose={() => setIsEditPatientModalOpen(false)}
        patient={patient}
      />
    </Container>
  );
};

const PatientDetails = React.memo(
  PatientDetailsComponent,
  (prevProps, nextProps) => prevProps.patient?.uuid === nextProps.patient?.uuid
);

export default PatientDetails;
